import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, CurrencyCircleDollar, ShieldCheck, ArrowSquareOut, Buildings, Ranking, Star, Globe
} from "@phosphor-icons/react";

function fmtBtc(val) {
  if (!val) return "0 BTC";
  if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M BTC`;
  if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K BTC`;
  return `${val.toFixed(0)} BTC`;
}

function TrustDots({ score }) {
  return (
    <div className="flex gap-[3px]" title={`Trust Score: ${score}/10`}>
      {[...Array(10)].map((_, i) => (
        <div key={i} className={`w-[5px] h-[5px] rounded-full ${i < score ? "bg-emerald-400" : "bg-zinc-800"}`} />
      ))}
    </div>
  );
}

function LiquidityGauge({ score, size = "md" }) {
  const color = score >= 70 ? "emerald" : score >= 40 ? "amber" : "red";
  const label = score >= 70 ? "High" : score >= 40 ? "Medium" : "Low";
  const w = size === "sm" ? "w-10" : "w-14";
  const h = size === "sm" ? "h-1" : "h-1.5";
  return (
    <div className="flex flex-col items-end gap-0.5" data-testid="liquidity-gauge" title={`Liquidity Score: ${score}/100`}>
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] font-data font-bold text-${color}-400`}>{score}</span>
        <span className={`text-[7px] px-1 py-0 rounded-sm bg-${color}-900/30 text-${color}-400 border border-${color}-800/30`}>{label}</span>
      </div>
      <div className={`${w} bg-zinc-800/60 rounded-full ${h} overflow-hidden`}>
        <div className={`h-full rounded-full bg-${color}-500/70 transition-all`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
    </div>
  );
}

function ExchangeRow({ exchange, maxVol }) {
  const pct = maxVol > 0 ? (exchange.volume_24h_btc / maxVol) * 100 : 0;
  const rankColors = ["text-amber-400", "text-zinc-300", "text-amber-700"];

  return (
    <div
      data-testid={`cex-row-${exchange.rank}`}
      className="px-4 py-3 border-b border-zinc-800/40 hover:bg-zinc-900/40 transition-colors"
    >
      <div className="flex items-center gap-3">
        <span className={`text-sm font-data font-bold w-8 text-center flex-shrink-0 ${exchange.rank <= 3 ? rankColors[exchange.rank - 1] : "text-zinc-600"}`}>
          {exchange.rank}
        </span>

        {exchange.image ? (
          <img src={exchange.image} alt={exchange.name} className="w-8 h-8 rounded-full flex-shrink-0 bg-zinc-800" loading="lazy" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
            <Buildings size={16} className="text-zinc-500" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-zinc-100">{exchange.name}</p>
            <a href={exchange.url} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-blue-400 flex-shrink-0">
              <ArrowSquareOut size={12} />
            </a>
          </div>
          <div className="flex items-center gap-2">
            {exchange.country && <span className="text-[10px] text-zinc-500">{exchange.country}</span>}
            {exchange.year_established && <span className="text-[10px] text-zinc-600">Est. {exchange.year_established}</span>}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-2 ml-11 flex-wrap">
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Trust</p>
          <div className="flex items-center gap-1.5">
            <TrustDots score={exchange.trust_score} />
            <span className="text-[10px] font-data text-zinc-500">{exchange.trust_score}/10</span>
          </div>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">24h Volume</p>
          <p className="text-xs font-data font-medium text-zinc-100">{fmtBtc(exchange.volume_24h_btc)}</p>
          <div className="w-20 bg-zinc-800/40 rounded-sm h-1.5 mt-0.5 overflow-hidden">
            <div className="h-full rounded-sm bg-blue-500/50 transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
          </div>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Normalized</p>
          <p className="text-xs font-data text-zinc-300">{fmtBtc(exchange.volume_24h_btc_normalized)}</p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Liquidity</p>
          <LiquidityGauge score={exchange.liquidity_score || 0} size="sm" />
        </div>
      </div>
    </div>
  );
}

export default function CexExchanges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchCex = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/exchanges/cex`);
      setData(resp.data);
    } catch (e) { console.error("Failed to fetch CEX:", e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchCex(); }, []);
  const exchanges = data?.exchanges?.slice(0, 10) || [];
  const maxVol = exchanges[0]?.volume_24h_btc || 1;

  return (
    <div className="p-5 max-w-[1200px]" data-testid="cex-page">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Buildings size={22} weight="bold" className="text-blue-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Top CEX</h1>
          </div>
          <p className="text-xs text-zinc-500 ml-[34px]">Top centralized exchanges ranked by trust score & 24h volume</p>
        </div>
        <button data-testid="refresh-cex-btn" onClick={() => fetchCex(true)} disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm">
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Updating..." : "Refresh"}
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="cex-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5"><CurrencyCircleDollar size={14} className="text-blue-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">Total Vol (Top 10)</span></div>
            <p className="text-lg font-data font-bold text-zinc-100">{fmtBtc(data.total_volume_btc)}</p>
          </div>
          {exchanges[0] && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
              <div className="flex items-center gap-2 mb-1.5"><Ranking size={14} className="text-amber-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">#1 Exchange</span></div>
              <p className="text-lg font-data font-bold text-zinc-100">{exchanges[0].name}</p>
              <p className="text-[10px] text-zinc-500">{fmtBtc(exchanges[0].volume_24h_btc)}</p>
            </div>
          )}
          {exchanges[1] && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
              <div className="flex items-center gap-2 mb-1.5"><Star size={14} className="text-zinc-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">#2 Exchange</span></div>
              <p className="text-lg font-data font-bold text-zinc-100">{exchanges[1].name}</p>
              <p className="text-[10px] text-zinc-500">{fmtBtc(exchanges[1].volume_24h_btc)}</p>
            </div>
          )}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5"><ShieldCheck size={14} className="text-emerald-400" /><span className="text-[9px] uppercase tracking-wider text-zinc-500">Max Trust Score</span></div>
            <p className="text-lg font-data font-bold text-zinc-100">{exchanges[0]?.trust_score || 0}/10</p>
          </div>
        </div>
      )}

      {loading && <div className="space-y-2">{[...Array(10)].map((_, i) => <Skeleton key={i} className="h-14 bg-zinc-900/50" />)}</div>}

      {!loading && exchanges.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="cex-table">
          <div className="grid grid-cols-[36px_1fr_100px_120px_100px_80px] items-center gap-3 px-4 py-2.5 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600">Exchange</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-center">Trust</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">24h Volume</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">Normalized</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">Liquidity</span>
          </div>
          {exchanges.map(e => <ExchangeRow key={e.rank} exchange={e} maxVol={maxVol} />)}
        </div>
      )}

      {data && (
        <div className="mt-4 pt-3 border-t border-zinc-800/40 flex items-center justify-between">
          <p className="text-[10px] text-zinc-600">Source: CoinGecko</p>
          <p className="text-[10px] text-zinc-600">Updated: {new Date(data.fetched_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
